import { useQuery } from '@tanstack/react-query';

import {
  aggregateIssuesQueryOptions,
  issueListQueryOptions,
} from '@/entities/issue';
import { calculatePeriodCounts } from '@/features/(signal)/signals/lib/signal-date-preset.utils';
import { getChangePercentage } from '@/features/(signal)/signals/lib/signal-issue.utils';
import { getTimezoneDayjs } from '@/utils/time';

import type { SignalAlertPreviewIssue } from '../../model';

type UseSignalAlertPreviewIssuesParams = {
  workspaceId: string;
  signals: string[];
  duration: number;
  filter: StructuredFilter;
};

/**
 * Signal Alert 프리뷰용 이슈 목록을 조회하고 계산하는 훅
 * useComputedIssueList와 유사한 패턴으로 동작
 */
export function useSignalAlertPreviewIssues({
  workspaceId,
  signals,
  duration,
  filter,
}: UseSignalAlertPreviewIssuesParams) {
  // changes 계산을 위해 duration * 2 기간의 데이터를 조회
  const now = getTimezoneDayjs();
  const filterWithDateRange: StructuredFilter = {
    ...filter,
    start_date: now
      .subtractVal(duration * 2, 'day')
      .startOf('hour')
      .toJSON(),
    end_date: now.endOf('day').toJSON(),
  };

  const { data: originIssues = [], isLoading: issueLoading } = useQuery(
    issueListQueryOptions({ workspaceId }),
  );

  const { data: issueAggregates = [], isLoading: aggregateLoading } = useQuery(
    aggregateIssuesQueryOptions({
      filter: filterWithDateRange,
      period: 'DAILY',
      workspaceId,
    }),
  );

  const isLoading = issueLoading || aggregateLoading;

  const selectedSignalSet = new Set(signals);
  // 빈 배열 = all issues (signal 필터링 skip)
  const isAllIssues = selectedSignalSet.size === 0;

  // originIssues를 name으로 빠르게 조회하기 위한 Map
  const issueMap = new Map(originIssues.map((issue) => [issue.name, issue]));

  // aggregate 데이터를 기준으로 순회 (aggregate-first approach)
  const previewIssues: SignalAlertPreviewIssue[] = issueAggregates
    .map((aggregate) => {
      // aggregate에 대응하는 원본 issue 찾기
      const issue = issueMap.get(aggregate.name);
      if (!issue || issue.hidden) return null;

      // all issues가 아닌 경우에만 signal_ids 필터링
      if (!isAllIssues) {
        const issueSignalIds = issue.signal_ids ?? [];
        if (!issueSignalIds.some((id) => selectedSignalSet.has(id))) {
          return null;
        }
      }

      // 기간별 카운트 계산 (공유 유틸 사용)
      const { selectedPeriodCount, comparisonPeriodCount } =
        calculatePeriodCounts(aggregate.values, { manipulateType: 'day', value: duration });

      // 선택 기간에 데이터가 없으면 제외
      if (selectedPeriodCount === 0) return null;

      // getChangePercentage(prev, current): prev=comparison, current=selected
      const changeRate = getChangePercentage(comparisonPeriodCount, selectedPeriodCount);

      return {
        name: issue.name,
        feedbackCount: selectedPeriodCount,
        changeRate,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .sort((a, b) => b.feedbackCount - a.feedbackCount);

  return { previewIssues, isLoading };
}
